<template>
    <modal @close_modal="close" :loading="loading">
      <template v-slot:buttons>
        <annotate-buttons
            v-if="asset"
            :in_modal="true"
            @stored="removeItemFromGrid"
            @set_loading="set_loading"
            :annotations="annotations"
            :asset_id_history="asset_id_history"
            :asset_id="asset_id_cached"
            :asset_in_validation_set="asset_in_validation_set"
            @annotate-limit-reached="handleAnnotationLimitReached"
            >
        </annotate-buttons>
      </template>
      <template v-slot:content>
        <row v-if="asset">
          <col9>
            <div v-if="annotationLimitReached">
              <show-alert :alert-message="annotationLimitReachedMessage"></show-alert>
            </div>
            <asset-messages-panel
              :messages="asset_messages"
              :title="$t('annotate.asset_messages_title')"
              :system-label="$t('annotate.asset_messages_system')"
              :user-label="$t('annotate.asset_messages_user')"
              :archive-label="$t('annotate.asset_messages_archive')"
              @archive="archiveAssetMessage"
            />
            <message-composer
              v-if="asset_id_cached"
              :asset-id="asset_id_cached"
              @added="onAssetMessageAdded"
            />
            <annotated-image
                v-if="asset_url"
                ref="annotatedImage"
                :zoom_enabled="true"
                :show_brightness_contrast="true"
                :sensitive-blur="sensitiveBlurForModalAsset"
                :image_url="asset_url"
                :annotations="annotations"
                :placement_label="pendingPlacement"
                @place_on_canvas="onPlaceOnCanvas"
                @image_loaded="onCanvasReady"
                @canvas_mousedown="canvas_mousedown"
                :is_embedding_loading="isSam3Loading"
                @update_coords="updateAnnotation"
                @delete_annotation="deleteAnnotation"
                @select="selectAnnotation"
                @unselect="unselectAnnotation"
                @add_annotation_deselect_others="add_annotation_deselect_others"
                v-model:contrast="contrast"
                v-model:brightness="brightness"
            ></annotated-image>
            <meta-grid :meta="meta" :asset-id="asset_id_cached" @meta-updated="onMetaUpdated"></meta-grid>
          </col9>
          <col3>
            <row>
              <col12>
                <v-btn color="primary" v-if="model_id && !model_results_shown" @click="runModel(model_id)">
                  Show model results
                </v-btn>
                <v-btn color="primary" v-else-if="model_id" @click="resetAnnotations">
                  Hide model results
                </v-btn>
                <autocomplete v-else-if="filtered_models.length > 0 && (is_admin || is_manager || is_global_admin)"
                    :placeholder="$t('annotate.placeholder')"
                    :prepend_icon="prepend_icon_run_model"
                    :options="filtered_models"
                    :name="modelDisplayLabel"
                    @select="runModel"
                    @updated_search_value="model_search = $event"
                    data-testid="annotate-run-model"
                ></autocomplete>
                <div
                  v-if="litert_inference_status"
                  data-testid="annotate-litert-status"
                  class="annotate-litert-status"
                >
                  {{ litert_inference_status }}
                </div>
              </col12>
            </row>
            <template v-if="showLlmAnnotateAiTools">
            <row>
              <col12>
                <v-switch
                    v-model="auto_boxes_enabled"
                    :label="$t('annotate.auto_boxes')"
                    color="primary"
                    :disabled="annotationLimitReached"
                    inset
                    hide-details
                ></v-switch>
              </col12>
            </row>
            <row v-if="auto_boxes_enabled">
              <col12>
                <v-text-field
                    v-model="auto_boxes_query"
                    :label="$t('annotate.auto_boxes_hint')"
                    dense
                    outlined
                    :disabled="annotationLimitReached || !asset_id"
                    @keyup.enter="runAutoBoxes"
                ></v-text-field>
                <v-btn
                    color="primary"
                    :disabled="annotationLimitReached || !asset_id || auto_boxes_loading"
                    :loading="auto_boxes_loading"
                    @click="runAutoBoxes"
                >
                  {{$t('annotate.auto_boxes_run')}}
                </v-btn>
                <div v-if="auto_boxes_status_updates && auto_boxes_status_updates.length > 0" style="margin-top: 8px; font-size: 12px; opacity: 0.85;">
                  <div v-for="(status, idx) in auto_boxes_status_updates" :key="'auto-box-modal-status-' + idx">
                    {{ status }}
                  </div>
                </div>
              </col12>
            </row>
            </template>
            <template v-if="show_suggest_text_tools">
            <row style="margin-top: 4px;">
              <col12>
                <v-btn
                    class="mt-1 mr-2"
                    color="primary"
                    outlined
                    small
                    :disabled="annotationLimitReached || !annotate_asset_id || text_boxes_loading"
                    :loading="text_boxes_loading"
                    @click="runSuggestTextBoxes"
                >
                  {{$t('annotate.suggest_text_boxes')}}
                </v-btn>
                <v-btn
                    class="mt-1"
                    color="primary"
                    outlined
                    small
                    :disabled="annotationLimitReached || !annotate_asset_id || text_boxes_loading"
                    :loading="text_boxes_loading"
                    @click="runSuggestTextRecognition"
                >
                  {{$t('annotate.suggest_text_recognition')}}
                </v-btn>
                <div v-if="text_boxes_status_updates && text_boxes_status_updates.length > 0" style="margin-top: 8px; font-size: 12px; opacity: 0.85;">
                  <div v-for="(status, idx) in text_boxes_status_updates" :key="'text-box-modal-status-' + idx">
                    {{ status }}
                  </div>
                </div>
                <text-suggestions-panel
                    class="mt-2"
                    :suggestions="text_suggestions"
                    :loading="text_suggestions_loading"
                    @accept="acceptTextSuggestions"
                    @reject="rejectTextSuggestions"
                    @accept-all="acceptAllTextSuggestions"
                    @reject-all="rejectAllTextSuggestions"
                />
              </col12>
            </row>
            </template>
            <template v-if="sam3_enabled">
            <row>
              <col12>
                <div style="font-weight: 600; margin-top: 8px;">{{ $t('annotate.sam3') }}</div>
                <div style="font-size: 12px; opacity: 0.85; margin-bottom: 8px;">{{ $t('annotate.sam3_click_hint') }}</div>
                <v-text-field
                    v-model="sam3_query"
                    :label="$t('annotate.sam3_hint')"
                    dense
                    outlined
                    :disabled="annotationLimitReached || !asset_id || sam3_loading"
                    @keyup.enter="runSam3TextSegment"
                ></v-text-field>
                <v-btn
                    color="primary"
                    :disabled="annotationLimitReached || !asset_id || sam3_loading"
                    :loading="sam3_loading"
                    @click="runSam3TextSegment"
                >
                  {{$t('annotate.sam3_run')}}
                </v-btn>
                <div v-if="sam3_status" style="margin-top: 8px; font-size: 12px; opacity: 0.85;">
                  {{ sam3_status }}
                </div>
              </col12>
            </row>
            </template>
            <row>
              <col12>
                <text-annotate-panel
                    class="mt-3"
                    :annotations="annotations"
                    :selected-id="selected_annotation_id"
                    @update-transcript="updateAnnotationTranscript"
                />
              </col12>
            </row>
            <row>
              <col12>
                <annotate-label-placement
                    :disabled="annotationLimitReached"
                    :pending-placement="pendingPlacement"
                    @label_selected="onLabelSelectedForPlacement"
                    @clear="clearPendingPlacement"
                ></annotate-label-placement>
                <annotate-label-groups @add_annotation="addAnnotationClearSelection" :type="'group'"></annotate-label-groups>
              </col12>
            </row>

            <row>
              <col12>
                <hr/>
              </col12>
            </row>

            <row>
              <col12>
                <v-btn v-if="hasAnnotationSuggestions && show_annotation_suggestions" @click="show_suggestions" color="primary" style="float:none; margin-left:0px; margin-top: 15px" id="show_annotation_suggestion" class="button">
                  {{$t('annotate.btn_show')}}
                </v-btn>
                <v-btn v-if="!show_annotation_suggestions" @click="hide_suggestions" color="primary" style="float:none; margin-left:0px; margin-top: 15px" id="hide_annotation_suggestion" class="button">
                  {{$t('annotate.btn_hide')}}
                </v-btn>
                <suggestion-match-log-info :logs="suggestion_match_logs" style="display:inline-flex; margin-top:15px; vertical-align:middle;" />
                <v-btn v-if="hasEncodedBoundingBoxes && !show_bounding_box_meta" @click="show_bbox_meta" color="primary" style="float:none; margin-left:0px; margin-top: 15px" id="show_bounding_box_meta" class="button">
                  {{$t('annotate.btn_show_meta')}}
                </v-btn>
                <v-btn v-if="show_bounding_box_meta" @click="hide_bbox_meta" color="primary" style="float:none; margin-left:0px; margin-top: 15px" id="hide_bounding_box_meta" class="button">
                  {{$t('annotate.btn_hide_meta')}}
                </v-btn>
                <annotations-list
                    @update_label="updateAnnotationLabel"
                    @clear="clearAnnotations"
                    @select="selectAnnotation"
                    @unselect="unselectAnnotation"
                    :annotations="annotations"
                    ></annotations-list>
              </col12>
              <col12>
                <refresh-button @click="resetAnnotations"></refresh-button>
              </col12>
            </row>
            <row>
              <col12>
                <hr/>
              </col12>
            </row>
            <row v-if="is_admin || is_manager || is_global_admin">
              <col12>
                <v-switch
                  style="margin-top: 0px; margin-left: 5px; padding-top: 0px"
                  inset
                  v-model="_in_validation_set"
                  :label="'in validation set'"
                  color="primary"
                  hide-details
                  density="compact"
              ></v-switch>
              </col12>
            </row>

            <row>
              <col12>
                <div data-testid="annotate-last-updated" :data-asset-id="asset_id_cached">
                {{$t('annotate.last_updated')}}<br />
                <div
                  v-if="asset_editors.length === 0"
                  data-testid="annotate-editors-empty"
                  class="text-caption text-medium-emphasis"
                >
                  {{$t('annotate.no_editors')}}
                </div>
                <div
                  v-for="editor in asset_editors"
                  :key="'editor-' + editor.user_id + '-' + editor.id"
                  data-testid="annotate-editor"
                >
                  <mutation :mutation="editor"></mutation>
                </div>
                </div>
              </col12>
            </row>
          </col3>
        </row>
        <loader :loading="saving"></loader>
      </template>
    </modal>
</template>

<script>
import Modal from "@/components/Modal";
import AnnotateMixin from "@/mixins/AnnotateMixin";
import AnnotatedImage from "@/components/AnnotatedImage";
import AnnotationsList from "@/components/AnnotationsList";
import AnnotateLabelPlacement from "@/components/AnnotateLabelPlacement.vue";
import AnnotateButtons from "@/components/AnnotateButtons";
import AnnotateLabelGroups from "@/components/AnnotateLabelGroups";
import Row from "@/components/layout/Row";
import Col9 from "@/components/layout/Col9";
import Col12 from "@/components/layout/Col12";
import LoadSettingsMixin from "@/mixins/LoadSettingsMixin";
import LoadModelsMixin from "@/mixins/LoadModelsMixin";
import Col3 from "@/components/layout/Col3";
import Mutation from "@/components/Mutation";
import RefreshButton from "@/components/form/RefreshButton";
import Autocomplete from "@/components/Autocomplete.vue";
import CurrentUserMixin from "@/mixins/CurrentUserMixin.vue";
import ShowAlert from "@/components/ShowAlert.vue";
import MetaGrid from "@/components/MetaGrid.vue";
import TextAnnotatePanel from "@/components/TextAnnotatePanel.vue";
import TextSuggestionsPanel from "@/components/TextSuggestionsPanel.vue";
import AssetMessagesPanel from "@/components/AssetMessagesPanel.vue";
import SuggestionMatchLogInfo from "@/components/SuggestionMatchLogInfo.vue";
import MessageComposer from "@/components/MessageComposer.vue";
import Loader from "@/components/layout/loader";
import Sam3Mixin from "@/mixins/Sam3Mixin";
import {
  getBlurUnlessMetaFromSettings,
  shouldBlurAssetMeta,
} from '@/utils/sensitiveImageBlur';
import { uniqueAssetEditors } from '@/utils/assetEditors';

export default {
  name: "AnnotateModal",
  components: {
    Autocomplete,
    RefreshButton,
    Mutation,
    Col3,
    Col12,
    Col9, Row, AnnotateLabelGroups, AnnotateButtons, AnnotateLabelPlacement, AnnotatedImage, Modal, ShowAlert, AnnotationsList, MetaGrid, Loader, TextAnnotatePanel, TextSuggestionsPanel, AssetMessagesPanel, MessageComposer, SuggestionMatchLogInfo},
  props: ['asset_id', 'model_id'],
  data() {
    return {
      'asset': null,
      'meta': '',
      'brightness': 0,
      'contrast': 0,
      'asset_url': false,
      'loading': true,
      'asset_id_history': [],
      'focus': false,
      'asset_id_cached': null,
      'model_results_shown': false,
      'show_annotation_suggestions': true,
      'show_bounding_box_meta' : false,
      'annotationLimitReached': false,
      'saving': false,
      '_in_validation_set': null,
    }
  },
  computed: {
    annotationLimitReachedMessage() {
      return this.getLimitMessage('annotate', 'ANNOTATION_LIMIT');
    },
    prepend_icon_run_model () {
      return this.run_model_loading ? 'fas fa-spinner fa-spin' : 'far fa-circle-play';
    },
    modelDisplayLabel() {
      return (item) => this.formatModelDisplay(item);
    },
    filtered_models() {
      return this.models.filter(model => {
        const search = this.model_search.toLowerCase();
        return !search || model.name.toLowerCase().includes(search);
      });
    },
    asset_editors () {
      return uniqueAssetEditors(this.mutations);
    },
    'select_selected' () {
      if(!this.focus) {
        return false;
      }
      return this.focus.tagName.toUpperCase() === 'INPUT'
    },
    hasAnnotationSuggestions() {
      return this.annotation_suggestions.length > 0;
    },
    asset_in_validation_set() {
      // Prefer the switch state so toggling validation then saving sends the new value.
      if (this._in_validation_set !== null && this._in_validation_set !== undefined) {
        return !!this._in_validation_set;
      }
      if (!this.asset) {
        return null;
      }
      return this.asset.in_validation_set === true;
    },
    sensitiveBlurForModalAsset() {
      const policy = getBlurUnlessMetaFromSettings(this.settings || []);
      let metaStr = '';
      if (this.asset && typeof this.asset.meta === 'string') {
        metaStr = this.asset.meta;
      } else if (this.meta && typeof this.meta === 'object') {
        metaStr = JSON.stringify(this.meta);
      }
      return shouldBlurAssetMeta(metaStr, policy, this.is_sensitive_image_blur_exempt);
    },
  },
  methods: {
    handleAnnotationLimitReached() {
      this.annotationLimitReached = true
    },
    runModel(model_or_model_id) {

      if(this.run_model_timeout) {
        clearTimeout(this.run_model_timeout);
      }

      if(!model_or_model_id) {
        this.resetAnnotations();
        this.run_model_loading = false
        return;
      }


      let model_id = model_or_model_id;
      if (typeof model_or_model_id === 'object') {
        model_id = model_or_model_id.id;
      }
      this.resetAnnotations();
      // Force a fresh inference pass for the newly selected model.
      this.model_annotations = false;
      // Store last model and asset id for websocket callback
      this.last_model_id = model_id;
      this.last_asset_id = this.asset_id_cached;
      this.loadModalAnnotations(model_id, this.asset_id_cached);
      this.model_results_shown = true
    },
    focusIn (event) {
      const el = event.target
      this.focus = el
    },
    focusOut () {
      this.focus = false
    },
    close() {
      this.asset_id_cached = null
      this.asset = null
      this.$emit('close_modal')
      // Clean up model run state
      this.last_model_id = null
      this.last_asset_id = null
    },
    removeItemFromGrid() {
      this.$emit('remove_item', this.asset_id_cached);
      this.close();
    },
    addEvents: function(e) {

      let activeElement = document.activeElement;
      let inputs = ['input', 'select', 'button', 'textarea'];

      if (activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) !== -1) {
          return false;
      }

      if (!this.select_selected && e.keyCode === 32) { //space
        e.stopPropagation();
        e.preventDefault();

        this.close()
      }
    },
    hide_bbox_meta() {
      this.annotations = JSON.parse(JSON.stringify(this.initial_annotations));
      this.show_bounding_box_meta = false;
    },
    set_loading(value) {
      this.saving = value;
    },
    onMetaUpdated(metaStr) {
      let parsed = {};
      if (metaStr) {
        try {
          parsed = JSON.parse(metaStr);
        } catch (e) {
          parsed = {};
        }
      }
      this.meta = parsed;
      if (this.asset) {
        this.asset['meta'] = metaStr;
      }
    },
    onCanvasReady() {
      this.refresh_annotations();
    },
    scheduleCanvasRefresh() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.refresh_annotations();
          const img = this.$refs.annotatedImage;
          if (img && typeof img.redrawAnnotations === 'function') {
            img.redrawAnnotations();
          }
        });
      });
    },
  },
  watch: {
    async asset_id_cached(n, o) {
      const shouldRun = !o;
      if (shouldRun) {
        let asset = await this.$api.asset.get(n);
        this.asset = asset;
        this._in_validation_set = asset?.in_validation_set === true;
      }
    },
    asset_id(newId, oldId) {
      if (newId == null || newId === oldId || oldId === undefined) {
        return;
      }
      this.asset_id_cached = newId;
      this.load();
    },
    loading(isLoading) {
      if (!isLoading) {
        this.scheduleCanvasRefresh();
      }
    },
  },
  mounted() {
    this.asset_id_cached = this.asset_id;
    this.load();
    document.addEventListener('focusin', this.focusIn)
    document.addEventListener('focusout', this.focusOut)
    window.addEventListener('keydown', this.addEvents);
  },
  beforeUnmount() {
    document.removeEventListener('focusin', this.focusIn)
    document.removeEventListener('focusout', this.focusOut)
    window.removeEventListener('keydown', this.addEvents);
  },
  mixins: [AnnotateMixin, LoadSettingsMixin, LoadModelsMixin, CurrentUserMixin, Sam3Mixin]
}
</script>

<style scoped>
.annotate-litert-status {
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.85;
}
</style>
